import { useEffect, useState, type RefObject } from 'react';
import { useLocation } from 'react-router-dom';
import { navConfig } from './navConfig';
import {
  isModuleDashboard,
  moduleById,
  moduleForPath,
  subModuleForPath,
  taskForPath,
} from './modulesConfig';
import { isTemplateId } from '../../lib/templates/isTemplateId';

/**
 * Route-change accessibility (PRO-0250, NF-10). On every location change it:
 *   1. derives the human title for the new route, and
 *   2. moves keyboard focus to the route's first heading (making it programmatically
 *      focusable with `tabIndex=-1`), falling back to the `main` landmark.
 *
 * The returned title is announced by `RouteAnnouncer` via an `aria-live` region and
 * mirrored onto the `main` `aria-label`, so screen-reader users are told where they
 * landed instead of being silently dropped at the top of a new view.
 */
export function titleForPath(pathname: string, complaintsEnabled = false): string {
  if (pathname === '/') {
    return 'Home';
  }
  // Account-scoped Profile Settings page (PRO-0740 / ADR-014 amendment, SF-2).
  if (pathname === '/account') {
    return 'Profile settings';
  }
  // Portal-ops platform tier (PRO-0440).
  if (pathname === '/platform/schools') {
    return 'Platform administration';
  }
  if (pathname === '/platform/settings') {
    return 'Platform settings';
  }
  // Comms settings tier (PRO-0440).
  if (pathname === '/settings' || pathname === '/settings/templates') {
    return 'Comms settings';
  }
  if (pathname === '/settings/templates/new') {
    return 'New template';
  }
  const manageMatch = /^\/settings\/templates\/([^/]+)$/.exec(pathname);
  if (manageMatch && isTemplateId(manageMatch[1]!)) {
    return 'Manage template';
  }
  if (pathname === '/settings/delivery') {
    return 'Delivery';
  }
  if (pathname === '/settings/approvals') {
    return 'Broadcast approvals';
  }

  const moduleId = moduleForPath(pathname);
  if (moduleId !== 'comms') {
    const module = moduleById(moduleId);
    if (isModuleDashboard(pathname, complaintsEnabled)) {
      return module.label;
    }
    const sub = subModuleForPath(pathname, complaintsEnabled);
    const task = taskForPath(pathname, complaintsEnabled);
    if (task) {
      return task.id === 'home' ? (sub?.label ?? task.label) : task.label;
    }
    if (sub) {
      return sub.label;
    }
    return 'Page not found';
  }

  const match = navConfig.find(
    (item) => item.status === 'active' && item.path !== '/' && pathname.startsWith(item.path),
  );
  return match ? match.label : 'Page not found';
}

export function useRouteTitle(
  mainRef: RefObject<HTMLElement | null>,
  complaintsEnabled = false,
): string {
  const location = useLocation();
  const [title, setTitle] = useState<string>(() => titleForPath(location.pathname, complaintsEnabled));

  useEffect(() => {
    setTitle(titleForPath(location.pathname, complaintsEnabled));

    const main = mainRef.current;
    if (!main) {
      return;
    }
    const heading = main.querySelector<HTMLElement>('h1, h2');
    const focusTarget = heading ?? main;
    if (!focusTarget.hasAttribute('tabindex')) {
      focusTarget.setAttribute('tabindex', '-1');
    }
    focusTarget.focus();
  }, [location.pathname, mainRef, complaintsEnabled]);

  return title;
}
